import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import useLocale from "../hooks/useLocale";
import { fetchInstrumentsList, fetchMarketStatus } from "../utils/fetchers";
import ProductPageHeader from "../components/ProductPage/ProductPageHeader";
import OnlyText from "../components/OnlyText";
import { ComponentContainer, LayoutGrid } from "../components/LayoutContainers";
import NestedTabs from "../components/NestedTabs/NestedTabs";
import { SkeletonTableTabs } from "../components/skeletons/instruments";
import { InstrumentContext } from "../providers/InstrumentContextProvider";
import { instrumentNamesDictionary } from "../locales/dictionaries/instruments";

export default function ProductsPage() {
    const locale = useLocale();
    const navigate = useNavigate();

    const { productType } = useParams();
    const [instrumentName, setInstrumentName] = useState(null);
    const { isLoading: isLoadingInstruments, data: instrumentsList } = useQuery({
        queryKey: ["instrumentsList"],
        queryFn: fetchInstrumentsList,
        select(data) {
            const list = {
                indices: {
                    descripcion: locale === "es" ? "Indices" : "Indices",
                    product_url: null,
                },
            };
    
            const sortedData = data
                .filter(item => item.mostrar)
                .sort((a, b) => a.codigo.localeCompare(b.codigo));
    
            for (const item of sortedData) {
                const descripcionEs = (item.descripcion["es"]).toLowerCase();
                const labelEs = instrumentNamesDictionary[descripcionEs]?.es || item.descripcion["es"];
    
                list[item.codigo.toLowerCase()] = {
                    descripcion: instrumentNamesDictionary[descripcionEs][locale] || labelEs,
                    product_url: item.product_url && item.product_url[locale],
                };
            }
    
            return list;
        },
    });
    

    useEffect(() => {
        if (instrumentsList) {
            // eslint-disable-next-line no-prototype-builtins
            if (!instrumentsList.hasOwnProperty(productType.toLowerCase())) {
                navigate("/error");
            } else {
                setInstrumentName(instrumentsList[productType.toLowerCase()].descripcion);
            }
        }
    }, [instrumentsList, productType]);

    const { isLoading: isloadingMarketStatus, data: marketStatus } = useQuery({
        queryKey: ["marketStatus"],
        queryFn: fetchMarketStatus,
    });

    useEffect(() => {
        const headerMob = document.querySelector(".header.--below-895");
        const headerDesk = document.querySelector(".header.--above-895");

        headerMob.classList.remove("--theme-light");
        headerMob.classList.add("--theme-dark");
        headerDesk.classList.remove("--theme-light");
        headerDesk.classList.add("--theme-dark");
    }, []);

    return (
        <ComponentContainer>
            <LayoutGrid spacingTop="0" spacingBottom="0" cols={1}>
                <ProductPageHeader slug={instrumentName} />
            </LayoutGrid>

            <LayoutGrid spacingTop="0" spacingBottom="0" cols={1}>
                {/* Titulo con badge de abierto o cerrado */}
                <OnlyText
                    title={instrumentName}
                    badges
                    hasOpenMarketBadge={marketStatus?.abierto || false}
                    refreshBadge={true}
                    classNames={"with-badges mb-1"}
                />
            </LayoutGrid>

            <LayoutGrid spacingTop="0" spacingBottom="4" cols={1}>
                {/* Argentina e Indices */}
                {isLoadingInstruments && instrumentName ? (
                    <SkeletonTableTabs />
                ) : (
                    <InstrumentContext.Provider value={{ instrumentName, setInstrumentName, instrumentsList }}>
                        <NestedTabs key={productType} currentInstrument={productType} instruments={instrumentsList} />
                    </InstrumentContext.Provider>
                )}
            </LayoutGrid>
        </ComponentContainer>
    );
}
